/**
 * A queue promise is a container for a promise that can be
 * executed at a later time.
 */
export abstract class QueueablePromise {
    readonly timestamp = Date.now();
    abstract execute(): Promise<any>;
}

/**
 * A promise queue contains any number of QueuePromise objects. These objects are stored within a PromiseQueue and executed
 * as quickly as possible in order. This is ideal in situations where a specific operation should be
 * applied in an ordered way while still making.
 */
export class PromiseQueue<T extends QueueablePromise> {
    private queue: T[] = [];
    private activePromise?: Promise<void> = undefined;
    private queueMaxSize: number | undefined = undefined;
    private isEnabled: boolean;

    /**
     * Constructs a new promise queue.
     * @param queueMaxSize An optional maximum size, when the max size is hit.
     *                     The older promises will be discarded.
     * @param enabled When false, the queue will not process any jobs. Assign `enabled` to true to start processing.
     */
    constructor(queueMaxSize?: number, enabled = true) {
        this.queueMaxSize = queueMaxSize;
        this.isEnabled = enabled;
    }

    /**
     * Enqueue a new promise.
     * @param promise A new promise to add to the queue.
     */
    enqueue(promise: T) {
        this.queue.push(promise);

        // If configured to do so, let's ensure the queue cannot grow too large by removing
        // older unprocessed work. This is great in situations where we know promises are
        // enqueued often and only the latest promises need to be executed.
        if (this.queueMaxSize !== undefined && this.queue.length > this.queueMaxSize) {
            const queueLength = this.queue.length - 1;
            for (let i = 0; i < queueLength; ++i) {
                this.queue.shift();
            }
        }

        if (!this.isEnabled) {
            return;
        }

        // If there's an active processing promise, it'll be picked up alongside the existing work.
        if (this.activePromise) {
            return;
        }
        // Otherwise we create a promise to handle this.
        this.activePromise = this.dequeue();
    }

    get enabled() {
        return this.isEnabled;
    }

    /**
     * Enable or disable the queue. When disabled, the queue will not process any jobs.
     * Disabling processing will not cancel any active promises.
     */
    set enabled(value: boolean) {
        if (!this.isEnabled && value && !this.activePromise && this.getRemainingQueueSize() > 0) {
            this.activePromise = this.dequeue();
        }
        this.isEnabled = value;
    }

    /**
     * @returns Returns true when work is being actively processed by this queue.
     */
    hasActivePromise() {
        return !!this.activePromise;
    }

    /**
     * @returns The number of unexecuted jobs remaining in the queue. Not including the active job.
     */
    getRemainingQueueSize() {
        return this.queue.length;
    }

    /**
     * Finalize the queue, any jobs that come in while this is in progress will result
     * in the promise being extended.
     */
    async finalize() {
        if (this.activePromise) return this.activePromise;
        return Promise.resolve();
    }

    /**
     * Once called will recursively resolve the jobs in the
     * queue until no more are available.
     */
    private async dequeue() {
        const item = this.queue.shift();
        // Is the queue empty.
        if (!item) {
            this.activePromise = undefined;
            return;
        }
        // Execute & process remaining messages.
        try {
            await item.execute();
        } catch (err) {
            console.log(err);
        } finally {
            await this.dequeue();
        }
    }
}
